"use strict";
// Excel cells copied to the clipboard become a clean table image (Yahoo has no
// table element). We draw the cells ourselves rather than take Excel's own
// bitmap: that one is screen resolution and carries gridlines and selection.
Object.defineProperty(exports, "__esModule", { value: true });
exports.replaceTables = exports.renderTableImage = exports.tableFitsImage = exports.wrapLine = exports.displaySize = exports.numericColumns = exports.excelTable = exports.parseExcelText = void 0;
// Excel's clipboard text: tab between cells, newline between rows, and a cell
// holding a line break or a quote is wrapped in quotes ("a""b").
const parseExcelText = (text) => {
    const rows = [];
    let row = [];
    let cell = '';
    let quoted = false;
    let i = 0;
    const src = text.replace(/\r\n?/g, '\n');
    while (i < src.length) {
        const ch = src[i];
        if (quoted) {
            if (ch === '"' && src[i + 1] === '"') {
                cell += '"';
                i += 2;
                continue;
            }
            if (ch === '"')
                quoted = false;
            else
                cell += ch;
            i += 1;
            continue;
        }
        if (ch === '"' && cell === '')
            quoted = true;
        else if (ch === '\t') {
            row.push(cell);
            cell = '';
        }
        else if (ch === '\n') {
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
        }
        else
            cell += ch;
        i += 1;
    }
    if (cell !== '' || row.length > 0) {
        row.push(cell);
        rows.push(row);
    }
    return rows;
};
exports.parseExcelText = parseExcelText;
// Cells copied from a sheet: at least one row with two cells or more.
const excelTable = (text) => {
    if (!text.includes('\t'))
        return null;
    const rows = (0, exports.parseExcelText)(text).map((row) => row.map((cell) => cell.trim()));
    while (rows.length > 0 && rows[rows.length - 1].every((cell) => cell === ''))
        rows.pop();
    if (rows.length === 0 || !rows.some((row) => row.length >= 2))
        return null;
    const width = Math.max(...rows.map((row) => row.length));
    return rows.map((row) => [...row, ...Array(width - row.length).fill('')]);
};
exports.excelTable = excelTable;
const NUMBER = /^[(（]?[+\-−▲△]?[¥$]?\d[\d,]*(\.\d+)?[%％倍x]?[)）]?$/;
const DASH = /^[-−–—―]$|^n\.?a\.?$|^n\.?m\.?$/i;
// A column is right-aligned when its cells below the header are numbers (or dashes).
const numericColumns = (rows) => rows[0].map((_, c) => {
    const cells = rows
        .slice(1)
        .map((row) => row[c])
        .filter((cell) => cell !== '');
    return c > 0 && cells.length > 0 && cells.every((cell) => NUMBER.test(cell) || DASH.test(cell));
});
exports.numericColumns = numericColumns;
const FONT_FAMILY = '"Noto Sans JP", "Yu Gothic", "Hiragino Sans", sans-serif';
const FONT_SIZE = 14;
const LINE = 21;
const PAD_X = 12;
const PAD_Y = 7;
const SCALE = 2;
// Yahoo lays an image out at its width/height (up to the column, ~650px), so a
// table image, drawn at SCALE x, is given its real size: crisp, text not doubled.
const displaySize = (image, kind) => kind === 'table' ? { ...image, width: Math.round(image.width / SCALE), height: Math.round(image.height / SCALE) } : image;
exports.displaySize = displaySize;
// A cell's text wraps past this width, so one long note doesn't make the image huge.
const MAX_TEXT_WIDTH = 260;
// Characters a line should not start with (、。）」 …): they stay on the line before.
const NO_LINE_START = /[、。，．,.)）」』】〕・ー々ゃゅょっャュョッ！？!?：:；;%％]/;
// Breaks one line to fit `max`: after a space or 、。 near the end when there is
// one, otherwise between any two characters.
const wrapLine = (line, max, measure) => {
    const chars = Array.from(line);
    const out = [];
    let current = '';
    for (const ch of chars) {
        if (current === '' || measure(current + ch) <= max || NO_LINE_START.test(ch)) {
            current += ch;
            continue;
        }
        const soft = Math.max(current.lastIndexOf(' '), current.lastIndexOf('、'), current.lastIndexOf('。'));
        if (soft > 0 && soft >= current.length - 8) {
            out.push(current.slice(0, soft + 1).trimEnd());
            current = current.slice(soft + 1).trimStart() + ch;
        }
        else {
            out.push(current);
            current = ch;
        }
    }
    out.push(current);
    return out;
};
exports.wrapLine = wrapLine;
// Same limits as the Word import: past them a table stays text (and the canvas
// would get too large to draw).
const tableFitsImage = (rows) => rows.length > 0 && rows.length <= 100 && rows[0].length <= 30;
exports.tableFitsImage = tableFitsImage;
// Draws the table at twice its size (crisp when Yahoo scales it) and returns a PNG.
const renderTableImage = async (rows) => {
    if (!(0, exports.tableFitsImage)(rows))
        throw new Error('table too large');
    // The web font comes in pieces by character range: load the ones this table uses.
    const text = rows.flat().join('');
    await document.fonts?.load(`400 ${FONT_SIZE}px "Noto Sans JP"`, text).catch(() => undefined);
    await document.fonts?.load(`700 ${FONT_SIZE}px "Noto Sans JP"`, rows[0].join('')).catch(() => undefined);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx)
        throw new Error('canvas unavailable');
    const font = (bold) => `${bold ? '700' : '400'} ${FONT_SIZE}px ${FONT_FAMILY}`;
    const lines = rows.map((row, r) => row.map((cell) => {
        ctx.font = font(r === 0);
        return cell.split('\n').flatMap((line) => (0, exports.wrapLine)(line, MAX_TEXT_WIDTH, (text) => ctx.measureText(text).width));
    }));
    const widths = rows[0].map((_, c) => Math.max(40, ...lines.map((row, r) => {
        ctx.font = font(r === 0);
        return Math.max(...row[c].map((line) => ctx.measureText(line).width)) + PAD_X * 2;
    })));
    const heights = lines.map((row) => Math.max(...row.map((cell) => cell.length)) * LINE + PAD_Y * 2);
    const width = Math.ceil(widths.reduce((a, b) => a + b, 0)) + 1;
    const height = Math.ceil(heights.reduce((a, b) => a + b, 0)) + 1;
    canvas.width = width * SCALE;
    canvas.height = height * SCALE;
    ctx.scale(SCALE, SCALE);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#eef2f6';
    ctx.fillRect(0, 0, width, heights[0]);
    const right = (0, exports.numericColumns)(rows);
    ctx.textBaseline = 'middle';
    let y = 0.5;
    lines.forEach((row, r) => {
        let x = 0.5;
        row.forEach((cell, c) => {
            ctx.font = font(r === 0);
            ctx.fillStyle = '#1d1d1f';
            // Number columns right-aligned, header included; other header cells centred.
            const align = right[c] ? 'right' : r === 0 && c > 0 ? 'center' : 'left';
            ctx.textAlign = align;
            const tx = align === 'center' ? x + widths[c] / 2 : align === 'right' ? x + widths[c] - PAD_X : x + PAD_X;
            const top = y + (heights[r] - cell.length * LINE) / 2;
            cell.forEach((line, l) => ctx.fillText(line, tx, top + LINE * l + LINE / 2));
            x += widths[c];
        });
        y += heights[r];
    });
    // Rules: a line under every row, a stronger one under the header, none between columns.
    ctx.strokeStyle = '#d5dbe2';
    ctx.lineWidth = 1;
    y = 0.5;
    heights.forEach((h, r) => {
        y += h;
        ctx.strokeStyle = r === 0 ? '#9aa5b1' : '#d5dbe2';
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    });
    ctx.strokeStyle = '#9aa5b1';
    ctx.beginPath();
    ctx.moveTo(0, 0.5);
    ctx.lineTo(width, 0.5);
    ctx.stroke();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob)
        throw new Error('canvas export failed');
    return new File([blob], 'table.png', { type: 'image/png' });
};
exports.renderTableImage = renderTableImage;
// A Word import gives each table as text rows (start..start+count-1) and cell by
// cell; each becomes one image block in place of its rows. A table whose image
// fails stays as text.
const replaceTables = async (items, tables, toImage) => {
    const out = [...items];
    let done = 0;
    let failed = 0;
    const ordered = [...tables].sort((a, b) => a.start - b.start);
    // From the last one, so the earlier positions stay right; captioned 表1, 表2 … in reading order.
    for (const table of [...ordered].reverse()) {
        try {
            const image = await toImage(table.rows);
            out.splice(table.start, table.count, { type: 'Img', ...image, alt: `表${ordered.indexOf(table) + 1}` });
            done += 1;
        }
        catch {
            failed += 1;
        }
    }
    return { items: out, done, failed };
};
exports.replaceTables = replaceTables;
